import React, { useEffect, useState } from "react"
import SbEditable from "storyblok-react"
import tw, { css } from "twin.macro"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faClock, faPlusCircle } from "@fortawesome/pro-duotone-svg-icons"
import styled from "styled-components"
import { graphql, useStaticQuery } from "gatsby"
import { PricingBlockStoryblok } from "../../../component-types-sb"
import { PricingPlan, usePricingData } from "../../contexts/PricingDataContext"
import { isUndefined } from "../../utils/typeGuards"
import useLocale from "../../hooks/useLocale"
import { Locale } from "../../../locales"
import LinkButton from "./LinkButton"
import Feature from "./Feature"
import LinkResolver from "./Link"
import PricingViewerSelector from "./PricingViewerSelector"


const BlockWrapper = styled.div(() => [
  tw`relative flex-1 bg-white shadow rounded-xl
  [&[data-featured='true']]:(
      desktop-xl:-mt-8!
      ring-blue ring-1
      [[data-theme='secondary'] &]:ring-blue-police
  )`,

  css`&[data-disabled='true'] {
      cursor: not-allowed;
      filter: grayscale(1) !important;
      button {
        pointer-events: none;
      }
  }`,
])

const FeaturedWrapper = tw.div`
  w-full
  desktop-xl:h-8
  text-white bg-blue
  font-bold text-18 leading-8 text-center uppercase
  rounded-t-xl
  [[data-theme='secondary'] &]:bg-blue-police
`

const ContentWrapper = tw.div`
  px-4 pt-6 pb-8
  space-y-4
  desktop-xl:pt-4
`

const PlanInfoWrapper = tw.div`
  flex flex-col
  px-4
  text-left
  text-24
  [[data-is-long='true'] &]:text-20
`

const PriceWrapper = tw.div`
  desktop-xl:h-24
  w-full
  mb-6
  text-center
  desktop-xl:text-left
`

const AltPrice = tw.span`
  text-30 [[data-is-long='true'] &]:text-24 font-heading font-normal text-gray-4 line-through
  mr-2
`

const MainPrice = tw.span`
  text-45 [[data-is-long='true'] &]:text-30 font-heading font-medium
`

const BillingCaption = tw.p`
  desktop-xl:text-13
  text-16 text-gray-2
  [span > span]:font-bold
`

const CtaTextWrapper = tw.div`
  desktop-xl:h-11
  space-y-4
  py-2
`

const SavingsText = tw.span`
  text-green text-13 font-bold uppercase
`

const FeaturesWrapper = tw.div`
  flex flex-col space-y-3
  px-4
  desktop-xl:px-0
  [&[data-show="false"]]:(
    hidden
    desktop-xl:flex
  )
`

const ShowFeaturesButton = tw.button`
  block
  desktop-xl:hidden
  space-x-2
  text-blue font-medium
  mx-auto
  [&[data-show="true"]]:(
    [svg]:rotate-[135deg]
  )
  [svg]:(
    transition-transform
    duration-300
    rotate-0
  )
`

const SaleTag = tw.span`
  text-14
  bg-green
  rounded-lg
  text-white
  px-[11px]
  py-[3px]
  font-bold
  ml-3
  h-[min-content]
  [&[data-yellow-badge="true"]]:(
    bg-yellow
  )
`

const ClockIcon = styled(FontAwesomeIcon)`
  --fa-primary-color: #ffffff;
  --fa-secondary-color: #f2c94c;
  --fa-secondary-opacity: 1;
`

const LimitedOfferBadge = ({ text }: { readonly text: string }) => (
  <div tw="absolute -right-5.75 -top-5">
    <ClockIcon
      icon={faClock}
      tw="absolute border rounded-full left-[-7.75px] top-[-7.75px]"
    />
    <span tw="text-[10px] bg-yellow text-white rounded-lg p-2 font-bold inline-flex">
      {text}
    </span>
  </div>
)

const useLocaleMappings = (): Record<string, string> => {
  const data = useStaticQuery(graphql`
    query LocaleMappings {
      allStoryblokDatasourceEntry(filter: { data_source: { eq: "locale-mappings" } }) {
        nodes {
          name
          value
        }
      }
    }
  `)

  // Convert the datasource entries into a key-value mapping
  return data.allStoryblokDatasourceEntry.nodes.reduce((acc: Record<Locale, string>, node: { name: string; value: string }) => {
    acc[node.name as Locale] = node.value
    return acc
  }, {})
}

const formatPrice = (
  price: number,
  localeCode: string,
  currency: string,
) => {

  const formatter = new Intl.NumberFormat(localeCode, {
    currency,
    minimumFractionDigits: 0,
    style: "currency",
  })

  return formatter.format(Math.round(price)).replace("US", "")
}

interface Props {
  readonly blok: PricingBlockStoryblok
  readonly plan: PricingPlan
  readonly selectAttendees: string
  readonly perMonthText: string
  readonly limitedOffer?: string
  readonly onlyYearly?: boolean
  readonly perYearText?: string
}

const PricingBlock: React.FC<Props> = ({
  blok,
  plan,
  selectAttendees,
  perMonthText,
  perYearText,
  limitedOffer,
  onlyYearly,
}) => {
  const { currency, yearlyBilling, getPrice, currentRate, onlyShowUsd, availablePlans, selectedPlans } = usePricingData()
  const { locale } = useLocale()

  // Resolve the localeCode using localeMappings
  const localeMappings = useLocaleMappings()
  // Fallback to "en-US" if localeCode is undefined
  const localeCode = localeMappings[locale] ?? "en-US"

  // Retrieve the currently selected pricing data for this plan:
  const selectedData = availablePlans[plan]?.[selectedPlans[plan]]

  const [showFeatures, setShowFeatures] = useState(false)
  const [isDesktopXL, setIsDesktopXL] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      setIsDesktopXL(window.innerWidth >= 1280)
    }
    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  useEffect(() => {
    setShowFeatures(isDesktopXL)
  }, [isDesktopXL])

  const billingCaptionText = {
    advanced: blok.billed_annually_text ?? "",
    premium: yearlyBilling
      ? blok.billed_annually_text ?? ""
      : blok.billed_monthly_text ?? "",
    standard: yearlyBilling
      ? blok.billed_annually_text ?? ""
      : blok.billed_monthly_text ?? "",
  }[plan].replace(/\d+(?:,\d+)?/u, (subscribers) => `<span>${subscribers}</span>`)

  const monthlyPrice = getPrice("monthly", plan)
  const normalPrice = getPrice("normal", plan)
  const annualPrice = getPrice("annual", plan)

  const priceText = onlyYearly ? perYearText ?? "" : perMonthText

  const planRecommended = Boolean(blok.most_popular_text)
  const hasPrices = normalPrice && annualPrice
  const isLong = currency === "SEK" || currency === "NOK" || currency === "DKK"

  const disabled = !yearlyBilling && Boolean(blok.disable)

  let customPriceContent: React.ReactNode = null
  if (blok.custom_price && !isUndefined(onlyShowUsd))
    customPriceContent = (
      <p>
        <MainPrice>
          {formatPrice(
            onlyShowUsd && blok.custom_price_US
              ? Number(blok.custom_price_US)
              : Number(blok.custom_price) * currentRate,
            localeCode,
            currency,
          )}
        </MainPrice>
        <span tw="ml-1">{priceText}</span>
      </p>
    )
  else if (isUndefined(onlyShowUsd))
    customPriceContent = <p tw="text-center">Loading...</p>
  else
    customPriceContent = (
      <>
        {hasPrices && (
          <p>
            {yearlyBilling && monthlyPrice !== annualPrice && (
              <AltPrice>
                {monthlyPrice !== null && formatPrice(monthlyPrice, localeCode, currency)}
              </AltPrice>
            )}
            <MainPrice>{formatPrice(normalPrice, localeCode, currency)}</MainPrice>
            <span tw="ml-1">{priceText}</span>
          </p>
        )}
      </>
    )

  const content = (
    <BlockWrapper data-disabled={disabled} data-featured={planRecommended} data-is-long={isLong}>
      {limitedOffer && planRecommended && yearlyBilling && <LimitedOfferBadge text={limitedOffer} />}
      {planRecommended && <FeaturedWrapper>{blok.most_popular_text}</FeaturedWrapper>}
      <PlanInfoWrapper tw="bg-blue-primary-1 px-11 py-8 [[data-featured='false'] &]:rounded-t-xl desktop-xl:min-h-[162px]">
        <p tw="my-0 text-30 font-medium mb-4 flex items-center">
          {blok.title}
          {yearlyBilling &&
          (blok.pricing_sale_badge_text ||
            (onlyShowUsd ? selectedData?.savings_percentage_us : selectedData?.savings_percentage)) && (
            <SaleTag data-yellow-badge={blok.pricing_sale_badge_yellow}>
              {blok.pricing_sale_badge_text || (currency === "USD"
                ? selectedData.savings_percentage_us
                : selectedData.savings_percentage)}
            </SaleTag>
          )}
        </p>
        <p tw="text-16">{blok.short_text}</p>
      </PlanInfoWrapper>
      <ContentWrapper>
        <PlanInfoWrapper>
          {blok.custom_price === "-1" && (
            <PriceWrapper tw="flex items-center justify-center">
              <span tw="text-26 text-center my-8 font-medium [[data-is-long='true'] &]:text-20">
                {blok.custom_price_text}
              </span>
            </PriceWrapper>
          )}
          {blok.custom_price !== "-1" && (
            <PriceWrapper>
              <span tw="desktop-xl:block leading-none h-[18px] [&[data-hidden='true']]:invisible">
                {blok.custom_price_text}
              </span>
              {customPriceContent}
              {!onlyYearly && (
                <BillingCaption>
                  <span dangerouslySetInnerHTML={{ __html: billingCaptionText }} />
                  {yearlyBilling &&
                    plan !== "advanced" &&
                    monthlyPrice &&
                    hasPrices &&
                    blok.save_text && (
                      <>
                        {" - "}
                        <SavingsText>
                          {`${blok.save_text} ${formatPrice((monthlyPrice - annualPrice) * 12, localeCode, currency)}`}
                        </SavingsText>
                      </>
                    )}
                </BillingCaption>
              )}
            </PriceWrapper>
          )}
          <PricingViewerSelector
            plan={plan}
            selectAttendees={selectAttendees}
            customMessage={blok.viewer_selector_text}
          />
          {blok.link && blok.link.length > 0 && (
            <CtaTextWrapper>
              <p tw="text-center text-16">
                <LinkResolver url={blok.link[0].url} styling="underline">
                  {blok.link[0].text}
                </LinkResolver>
              </p>
            </CtaTextWrapper>
          )}
          {blok.button && (
            <LinkButton tw="w-full mt-4 desktop-xl:mt-0" button={{ ...blok.button[0] }} />
          )}
        </PlanInfoWrapper>
        <ShowFeaturesButton data-show={showFeatures} onClick={() => setShowFeatures((prev) => !prev)}>
          <FontAwesomeIcon icon={faPlusCircle} />
          <span>Show features</span>
        </ShowFeaturesButton>
        <FeaturesWrapper data-show={showFeatures}>
          {blok.features?.map((feature) => {
            if (feature.component === "line") return <hr key={feature._uid} />
            return <Feature key={feature._uid} blok={feature} circleCheck />
          })}
        </FeaturesWrapper>
      </ContentWrapper>
    </BlockWrapper>
  )

  return <SbEditable content={blok}>{content}</SbEditable>
}

export default PricingBlock
